import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Award, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';


const About = () => {
    const stats = [
        { label: 'Spas Listed', value: '1000+' },
        { label: 'Areas Covered', value: '50+' },
        { label: 'Happy Users', value: '50000+' },
        { label: 'Reviews', value: '150000+' },
    ];

    const values = [
        {
            icon: Heart,
            title: 'Wellness First',
            description: 'We believe in the transformative power of self-care and prioritize authentic wellness experiences.'
        },
        {
            icon: Users,
            title: 'Community Driven',
            description: 'Our platform is built on genuine reviews and recommendations from the Bangalore wellness community.'
        },
        {
            icon: Award,
            title: 'Quality Assured',
            description: 'Every spa on our platform is vetted for quality, hygiene, and professional standards.'
        },
        {
            icon: Sparkles,
            title: 'Personalized',
            description: 'We help you find the perfect spa experience tailored to your unique preferences and needs.'
        },
    ];

    return (
        <div className="min-h-screen bg-[#F9F6F0] pt-24 noise-overlay" data-testid="about-page">
            {/* Hero Section */}
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-4 py-1.5 bg-[#C8A86B]/10 text-[#C8A86B] text-sm font-medium tracking-widest-custom uppercase rounded-full mb-6">
                                Our Story
                            </span>
                            <h1 className="font-heading text-4xl sm:text-5xl text-[#4A4E4D] mb-6 leading-tight">
                                Connecting You to <br />
                                <span className="text-[#C8A86B]">Wellness & Serenity</span>
                            </h1>
                            <p className="text-[#4A4E4D]/70 text-lg leading-relaxed mb-6">
                                SAPA was born from a simple idea: everyone deserves access to quality spa and wellness experiences. 
                                In the bustling city of Bangalore, we saw the need for a trusted guide to help people discover their perfect escape.
                            </p>
                            <p className="text-[#4A4E4D]/70 text-lg leading-relaxed">
                                Today, we're proud to be Bangalore's most comprehensive spa directory, connecting wellness seekers 
                                with exceptional spas that cater to every preference and budget.
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="aspect-[4/5] rounded-3xl overflow-hidden">
                                <img
                                    src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80"
                                    alt="Spa interior"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-lg border border-[#E0DAD0]">
                                <p className="font-heading text-3xl text-[#C8A86B] mb-1">Since 2020</p>
                                <p className="text-[#4A4E4D]/60 text-sm">Serving Bangalore</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-12 bg-[#4A4E4D]">
                <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="text-center"
                            >
                                <p className="font-heading text-4xl sm:text-5xl text-[#C8A86B] mb-2">{stat.value}</p>
                                <p className="text-white/70 text-sm uppercase tracking-widest-custom">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-3 py-1 bg-[#D4DED5]/50 text-[#4A4E4D] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                            What We Stand For
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                            Our Values
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="bg-white rounded-2xl p-6 border border-[#E0DAD0] hover:border-[#C8A86B] transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-full bg-[#C8A86B]/10 flex items-center justify-center mb-4">
                                    <value.icon className="w-6 h-6 text-[#C8A86B]" />
                                </div>
                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-2">{value.title}</h3>
                                <p className="text-[#4A4E4D]/60 text-sm leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission Section */}
            <section className="section-padding bg-[#EBE6DE]/30">
                <div className="container-wide mx-auto">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="inline-block px-3 py-1 bg-[#C8A86B]/10 text-[#C8A86B] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                                Our Mission
                            </span>
                            <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D] mb-6">
                                Making Wellness Accessible
                            </h2>
                            <p className="text-[#4A4E4D]/70 text-lg leading-relaxed mb-8">
                                We're on a mission to democratize wellness in Bangalore. Whether you're looking for a quick massage 
                                after a long day or a luxurious spa retreat, SAPA helps you find the perfect experience without the hassle.
                            </p>
                            <Link 
                                to="/spas" 
                                className="btn-gold inline-block"
                                data-testid="explore-spas-btn"
                            >
                                Start Exploring
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="section-padding">
                <div className="container-wide mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <span className="inline-block px-3 py-1 bg-[#D4DED5]/50 text-[#4A4E4D] text-xs font-medium tracking-widest-custom uppercase rounded-full mb-4">
                            The People Behind SAPA
                        </span>
                        <h2 className="font-heading text-3xl sm:text-4xl text-[#4A4E4D]">
                            Our Team
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {[
                            { name: 'Priya Sharma', role: 'Founder & CEO', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&q=80' },
                            { name: 'Arjun Mehta', role: 'Head of Curation', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80' },
                            { name: 'Ananya Rao', role: 'Community Manager', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=80' },
                        ].map((member, index) => (
                            <motion.div
                                key={member.name}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                className="text-center"
                            >
                                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#D4DED5]">
                                    <img
                                        src={member.image}
                                        alt={member.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <h3 className="font-heading text-xl text-[#4A4E4D] mb-1">{member.name}</h3>
                                <p className="text-[#C8A86B] text-sm">{member.role}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;
